import React, { lazy } from "react";
import ReactDOM from "react-dom/client";
// Dev-only motion gallery — lazy so it never enters production chunks.
const MotionPreview = lazy(() => import("./motionKit/MotionPreview"));
const AiParseOverlay = lazy(() => import("./components/shared/AiParseOverlay"));
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Toaster as HotToaster } from "react-hot-toast";
import "./style.css";
import "./components/motion/motion.css";
import "./motionKit/fkPrimitives.css";
import { MotionConfig } from "framer-motion";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "react-redux";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { GoogleOAuthProvider } from "@react-oauth/google";

// Infrastructure / providers — always needed, keep eager.
import store from "./redux/store";
import SideBarProvider from "./Providers/SideBarProvider";
import ThemeProvider from "./Providers/ThemeProvider";
import { DarkModeProvider } from "./context/DarkModeContext";
import { FeatureFlagProvider } from "./context/FeatureFlagContext";
import { WalkthroughProvider, TourOverlay } from "./walkthrough";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import ErrorPage from "./ErrorPage";
import RouteFallback from "./components/shared/RouteFallback";

// Layout roots wrap every route via <Outlet/> — keep eager.
import OpenRoot from "./Route/OpenRoot";
import AuthRoot from "./Route/AuthRoot";
import MagicRoot from "./Route/MagicRoot.jsx";

// First-paint public pages — keep eager to avoid a loading flash on entry.
import Home from "./Pages/Home";
import Login from "./Pages/Login";

// Route pages — lazy-loaded so each lands in its own chunk and stays out of
// the initial bundle. Heavy deps (docx, jspdf, html2canvas, chart.js, quill)
// live in these pages and are now only fetched when their route is visited.
const Contact = lazy(() => import("./Pages/Contact"));
const PlanPage = lazy(() => import("./Pages/PlanPage"));
const FeaturePage = lazy(() => import("./Pages/FeaturePage"));
const Users = lazy(() => import("./Pages/Admin/Pages/Users"));
const Setting = lazy(() => import("./Pages/Admin/Pages/Setting"));
const AddUser = lazy(() => import("./Pages/Admin/Pages/AddUser"));
const PricePage = lazy(() => import("./Pages/User/Pages/PricePage"));
const AllTransaction = lazy(() => import("./Pages/Admin/Pages/AllTransaction"));
const IndividualTransaction = lazy(() => import("./Pages/Admin/Pages/IndividualTransaction"));
const ServicesAdd = lazy(() => import("./Pages/Admin/Pages/ServicesAdd"));
const SuccessPage = lazy(() => import("./Pages/User/Pages/SuccessPage"));
const GenerateContent = lazy(() => import("./Pages/User/Pages/GenerateContent"));
const ContentRequest = lazy(() => import("./Pages/User/Pages/ContentRequest"));
const MyContent = lazy(() => import("./Pages/User/Pages/Components/MyContent"));
const Session = lazy(() => import("./Pages/Session"));
const OrderTracking = lazy(() => import("./Pages/User/Pages/OrderTracking"));
const QuoteForm = lazy(() => import("./Pages/User/Pages/QuoteForm"));
const FailurePage = lazy(() => import("./Pages/User/Pages/FailurePage"));
const MailPage = lazy(() => import("./Pages/MailPage"));
const SendEmail = lazy(() => import("./Pages/SendEmail.jsx"));
const FetchThreadPage = lazy(() => import("./Pages/FetchThreadPage.jsx"));
const CompleteQuoteDetail = lazy(() => import("./Pages/Quotes/CompleteQuoteDetail.jsx"));
const AllQuotes = lazy(() => import("./Pages/Quotes/AllQuotesListing.jsx"));
const IncompleteQuoteDetailPage = lazy(() => import("./Pages/Quotes/IncompleteQuoteDetail.jsx"));
const AirlineRateRequestManager = lazy(() => import("./Pages/Rates/AirlineRateRequestManager.jsx"));
const AllQuotesListing = lazy(() => import("./Pages/Quotes/AllQuotesListing.jsx"));
const QuoteAirlineResponseListing = lazy(() => import("./Pages/Admin/Pages/QuoteAirlineResponseListing.jsx"));
const AgentRateRequestManager = lazy(() => import("./Pages/Rates/AgentRateRequestManager.jsx"));
const ShipperThread = lazy(() => import("./Pages/Admin/Pages/ShipperThread.jsx"));
const QuoteDashboard = lazy(() => import("./Pages/Quotes/QuoteDashboard.jsx"));
const QuoteComparisonTable = lazy(() => import("./Pages/Rates/QuoteComparisonTable.jsx"));
const QuoteAllDetails = lazy(() => import("./Pages/Quotes/QuoteAllDetails.jsx"));
const AllUpdates = lazy(() => import("./Pages/Notifications/AllUpdates.jsx"));
const Airlines = lazy(() => import("./Pages/Airlines/Airlines.jsx"));
const AddEditShowAirline = lazy(() => import("./Pages/Admin/Pages/AddEditShowAirline.jsx"));
const AwbStock = lazy(() => import("./Pages/AwbStock/AwbStock.jsx"));
const AddEditShowUser = lazy(() => import("./Pages/Admin/Pages/AddEditShowUser.jsx"));
const AllTemplates = lazy(() => import("./Pages/Admin/Pages/AllTemplates.jsx"));
const AddEditShowTemplate = lazy(() => import("./Pages/Admin/Pages/AddEditShowTemplate.jsx"));
const FreightCalculator2 = lazy(() => import("./Pages/Rates/FreightCalculator2.jsx"));
const Dashboard = lazy(() => import("./Pages/Admin/Pages/Dashboard.jsx"));
const GenerateDocuments = lazy(() => import("./Pages/Documents/GenerateDocuments.jsx"));
const TestComp = lazy(() => import("./Pages/Test.jsx"));
const CollectionVerification = lazy(() => import("./Pages/TestUser.jsx"));
const ShipperVerification = lazy(() => import("./Pages/ShipperVerification.jsx"));
const TagQuotesListing = lazy(() => import("./Pages/Tags/TagQuotesListing.jsx"));
const ManualQueriesPage = lazy(() => import("./Pages/ManualQueries/ManualQueriesPage.jsx"));
const ManualQueryDetail = lazy(() => import("./Pages/ManualQueries/ManualQueryDetail.jsx"));

const queryClient = new QueryClient();

const SideBarProviderRoot = () => (
  <SideBarProvider>
    <FeatureFlagProvider>
      <AuthRoot />
    </FeatureFlagProvider>
  </SideBarProvider>
);

const appRouter = createBrowserRouter([
  {
    path: "/",
    element: <OpenRoot />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <Home />,
      },
      {
        path: "/new",
        element: <QuoteDashboard />,
      },
      {
        path: "/intermediateSession",
        element: <Session />
      },
      {
        path: "/myOrder",
        element: <OrderTracking />,
      },
      {
        path: "/loginPage",
        element: <Login />,
      },
      // Dev-only motion gallery (Remotion compositions used for loading
      // states). Never registered in production builds.
      ...(process.env.NODE_ENV === "development"
        ? [
            {
              path: "/dev/motion",
              element: <MotionPreview />,
            },
            {
              /* Renders the overlay over MOCK PANEL CONTENT on purpose. The
                 overlay was once semi-transparent, and against a blank page that
                 looked fine — it was only over real fields that it showed two
                 forms ghosting through each other. A harness with nothing behind
                 it cannot catch that. */
              path: "/dev/parse-overlay",
              element: (
                <div style={{ padding: 24, background: "#eef2ff", minHeight: "100vh" }}>
                  <div
                    style={{
                      position: "relative",
                      width: 420,
                      height: 560,
                      overflow: "hidden",
                      borderRadius: 16,
                      background: "#fff",
                      boxShadow: "0 10px 40px -12px rgba(15,23,42,.25)",
                    }}
                  >
                    {[
                      ["Origin", "HR4 8DS"],
                      ["Destination", "DXB"],
                      ["Receiver Contact Name", "Mahan Bajeevan"],
                      ["Receiver Contact Email", "ops@example.com"],
                      ["Receiver Contact Phone", "+971 50 000 0000"],
                      ["Gross weight", "576 kg"],
                    ].map(([k, v]) => (
                      <div key={k} style={{ padding: "14px 18px" }}>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{k}</div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{v}</div>
                      </div>
                    ))}
                    <AiParseOverlay open onCancel={() => {}} />
                  </div>
                </div>
              ),
            },
          ]
        : []),
      // {
      //   path: "/register",
      //   element: <Register />,
      // },
      {
        path: "/contact",
        element: <Contact />,
      },
      // {
      //   path: "/scade",
      //   element: <ScadeFlowExecution/>,
      // },
      {
        path: "/pricing",
        element: <PlanPage />,
      },
      {
        path: "/features",
        element: <FeaturePage />,
      },
      {
        path: "/emails",
        element: <MailPage />,
      },
      {
        path: "/send-email",
        element: <SendEmail />,
      },
      {
        path: "/fetch-thread",
        element: <FetchThreadPage />,
      },
      {
        path: "/completeQuery",
        element: <CompleteQuoteDetail />,
      },
      {
        path: "/incompleteQuery",
        element: <IncompleteQuoteDetailPage />,
      },
      {
        path: "/my-quotes",
        element: <AllQuotes />,
      },
      {
        path: "/verify/:id",
        element: <TestComp />
      },
      {
        path: "/verify",
        element: <TestComp />
      },
      {
        path: "/agent",
        element: <MagicRoot />,
        children: [
          {
            path: "verify",
            element: <CollectionVerification />,
          },
        ]
      },
      {
        path: "/shipper",
        element: <MagicRoot />,
        children: [
          {
            path: "verify",
            element: <ShipperVerification />
          },
        ]
      },
      // {
      //   path: "/user/dashboard",
      //   element: <SideBarProviderRoot><User /></SideBarProviderRoot>
      // },
      {
        path: "/user",
        element: <SideBarProviderRoot />, // must use <SideBarProviderRoot/>
        children: [
          // {
          //   path: "/user/dashboard",
          //   //Absolute route path "/" nested under path "/user" is not valid. An absolute child route path must start with the combined path of all its parent routes i.e. "/user" instead of "/".
          //   element: <User />
          // },
          // {
          //   path: "/user/company/add",
          //   element: <AddCompany />,
          // },
          // {
          //   path: "/user/company/edit",
          //   element: <AddCompany />,
          // },
          {
            path: "/user/price",
            element: <PricePage />,
          },
          {
            path: "/user/content",
            element: <GenerateContent />,
          },
          {
            path: "/user/content/query",
            element: <ContentRequest />,
          },
          {
            path: "/user/generatedcontent",
            element: <MyContent />,
          },
          {
            path: "/user/Success",
            element: <SuccessPage />,
          },
          {
            path: "/user/cancel",
            element: <PricePage />,
          },
          {
            path: "/user/profile",
            element: <Setting />,
          },
          // {
          //   path: "/user/myAnalysis",
          //   element: <MyAnalysis />
          // },
          {
            path: "/user/profile/edit",
            element: <AddUser />
          },
          {
            path: "/user/transactions",
            element: <AllTransaction />,
          },
          {
            path: "/user/transactions/transaction",
            element: <IndividualTransaction />,
          },
          {
            path: "/user/freight-form",
            element: <QuoteForm />,
          },
          {
            path: "/user/my-quotes",
            element: <AllQuotes />,
          },
          {
            path: "/user/detail-quote/:id",
            element: <OrderTracking />,
          },
          {
            path: "/user/success",
            element: <SuccessPage />,
          },
          {
            path: "/user/cancel",
            element: <FailurePage />,
          },
        ],
      },
      {
        path: "/admin",
        element: <SideBarProviderRoot />,
        children: [
          {
            path: "/admin/profile",
            element: <Setting />,
          },
          {
            path: "/admin/dashboard",
            element: <Dashboard />,
          },
          {
            path: "/admin/users",
            element: <Users />,
          },
          // {
          //   path: "/admin/calculator",
          //   element: <FreightCalculator2 />,
          // },
          {
            path: "/admin/:id/calculator2",
            element: <FreightCalculator2 />,
          },
          {
            path: "/admin/services/add",
            element: <ServicesAdd />,
          },
          // {
          //   path: "/admin/users/:id",
          //   element: <AdminDetailedPage />,
          // },
          {
            path: "/admin/users/add",
            element: <AddUser />,
          },
          {
            path: "/admin/users/edit",
            element: <AddUser />,
          },
          {
            path: "/admin/admins",
            element: <Users />,
          },
          {
            path: "/admin/admins/add",
            element: <AddUser />,
          },
          {
            path: "/admin/admins/:id",
            element: <AddEditShowUser />,
          },
          {
            path: "/admin/operators",
            element: <Users />,
          },
          {
            path: "/admin/operators/add",
            element: <AddUser />,
          },
          {
            path: "/admin/operators/:id",
            element: <AddEditShowUser />,
          },
          {
            path: "/admin/agents",
            element: <Users />,
          },
          {
            path: "/admin/agents/add",
            element: <AddUser />,
          },
          {
            path: "/admin/agents/:id",
            element: <AddEditShowUser />,
          },
          {
            path: "/admin/templates",
            element: <AllTemplates />,
          },
          {
            path: "/admin/templates/:id",
            element: <AddEditShowTemplate />,
          },
          // {
          //   path: "/admin/users/content",
          //   element: <Content />,
          // },
          // {
          //   path: "/admin/company",
          //   element: <ManageCompanies />,
          // },
          // {
          //   path: "/admin/company/details",
          //   element: <IndividualCompanyDetails />,
          // },
          // {
          //   path: "/admin/company/edit",
          //   element: <AddCompany />,
          // },
          {
            path: "/admin/transactions",
            element: <AllTransaction />,
          },
          {
            path: "/admin/transactions/transaction",
            element: <IndividualTransaction />,
          },
          // {
          //   path: "/admin/users/content/:id",
          //   element: <ContentDetail />,
          // },
          // {
          //   path: "/admin/allqueries",
          //   element: <RequestedPosts />,
          // },
          // {
          //   path: "/admin/allposts",
          //   element: <RequestedPosts />,
          // },
          {
            path: "/admin/profile/edit",
            element: <AddUser />
          },
          {
            path: "/admin/all-quotes",
            element: <AllQuotes />,
          },
          {
            path: "/admin/notifications",
            element: <AllUpdates />,
          },
          {
            path: "/admin/template/add",
            element: <AddEditShowTemplate />,
          },
          {
            path: "/admin/quotes-airline-response",
            element: <QuoteAirlineResponseListing />,
          },
          {
            path: "/admin/complete-quote/:id",
            element: <CompleteQuoteDetail />
          },
          {
            path: "/admin/incomplete-query/:id",
            element: <IncompleteQuoteDetailPage />
          },
          {
            path: "/admin/detail-quote/:id",
            element: <OrderTracking />,
          },
          {
            path: "/admin/incomplete-queries",
            element: <AllQuotesListing />,
          },
          {
            path: "/admin/:id/rate-request",
            element: <AirlineRateRequestManager />,
          },
          {
            path: "/admin/:id/generate-documents",
            element: <GenerateDocuments />,
          },
          // {
          //   path: "/admin/agent-thread",
          //   element: <AgentThread />,
          // },
          {
            path: "/admin/:id/agent-rates",
            element: <AgentRateRequestManager />,
          },
          // {
          //   path: "/admin/airline-response",
          //   element: <AirlineResponse />,
          // },
          {
            path: "/admin/airlines",
            element: <Airlines />,
          },
          {
            path: "/admin/airlines/add",
            element: <AddEditShowAirline />,
          },
          {
            path: "/admin/airlines/:id",
            element: <AddEditShowAirline />,
          },
          {
            // Admins and superAdmins had no AWB stock route at all, so the
            // sidebar entry and the copilot's own `awbStock` target both
            // resolved to a dead URL for anyone who was not an operator.
            path: "/admin/awb-stock",
            element: <AwbStock />,
          },
          {
            path: "/admin/shipper-thread",
            element: <ShipperThread />,
          },
          {
            path: "/admin/quote/comparison-page",
            element: <QuoteComparisonTable />,
          },
          {
            path: "/admin/newQuotePage/:id",
            element: <QuoteAllDetails />,
          },
          {
            path: "/admin/manual-queries",
            element: <ManualQueriesPage />,
          },
          {
            path: "/admin/manual-query/:id",
            element: <ManualQueryDetail />,
          },
          {
            path: "/admin/manual-query/:id/calculator",
            element: <FreightCalculator2 />,
          },
        ],
      },
      {
        path: "/operator",
        element: <SideBarProviderRoot />,
        children: [
          {
            path: "/operator/profile",
            element: <Setting />,
          },
          {
            path: "/operator/dashboard",
            element: <Dashboard />,
          },
          {
            path: "/operator/agents",
            element: <Users />,
          },
          {
            path: "/operator/agents/add",
            element: <AddUser />,
          },
          {
            path: "/operator/agents/:id",
            element: <AddEditShowUser />,
          },
          {
            path: "/operator/:id/calculator2",
            element: <FreightCalculator2 />,
          },
          {
            path: "/operator/transactions",
            element: <AllTransaction />,
          },
          {
            path: "/operator/transactions/transaction",
            element: <IndividualTransaction />,
          },
          {
            path: "/operator/profile/edit",
            element: <AddUser />
          },
          {
            path: "/operator/all-quotes",
            element: <AllQuotes />,
          },
          {
            path: "/operator/notifications",
            element: <AllUpdates />,
          },
          {
            path: "/operator/quotes-airline-response",
            element: <QuoteAirlineResponseListing />,
          },
          {
            path: "/operator/complete-quote/:id",
            element: <CompleteQuoteDetail />
          },
          {
            path: "/operator/incomplete-query/:id",
            element: <IncompleteQuoteDetailPage />
          },
          {
            path: "/operator/detail-quote/:id",
            element: <OrderTracking />,
          },
          {
            path: "/operator/incomplete-queries",
            element: <AllQuotesListing />,
          },
          {
            path: "/operator/tags/:tagId",
            element: <TagQuotesListing />,
          },
          {
            path: "/operator/:id/rate-request",
            element: <AirlineRateRequestManager />,
          },
          {
            path: "/operator/:id/generate-documents",
            element: <GenerateDocuments />,
          },
          {
            path: "/operator/:id/agent-rates",
            element: <AgentRateRequestManager />,
          },
          {
            path: "/operator/airlines",
            element: <Airlines />,
          },
          {
            path: "/operator/airlines/add",
            element: <AddEditShowAirline />,
          },
          {
            path: "/operator/airlines/:id",
            element: <AddEditShowAirline />,
          },
          {
            path: "/operator/awb-stock",
            element: <AwbStock />,
          },
          {
            path: "/operator/shipper-thread",
            element: <ShipperThread />,
          },
          {
            path: "/operator/quote/comparison-page",
            element: <QuoteComparisonTable />,
          },
          {
            path: "/operator/newQuotePage/:id",
            element: <QuoteAllDetails />,
          },
          {
            path: "/operator/manual-queries",
            element: <ManualQueriesPage />,
          },
          {
            path: "/operator/manual-query/:id",
            element: <ManualQueryDetail />,
          },
          {
            path: "/operator/manual-query/:id/calculator",
            element: <FreightCalculator2 />,
          },
          // Templates — operators reach these via handleRole('operator') nav
          // (list / detail / add). Without them the pages 404'd for operators.
          {
            path: "/operator/templates",
            element: <AllTemplates />,
          },
          {
            path: "/operator/templates/:id",
            element: <AddEditShowTemplate />,
          },
          {
            path: "/operator/template/add",
            element: <AddEditShowTemplate />,
          },
        ],
      },
    ]
  }
]);

const portalDiv = document.getElementById("app");
ReactDOM.createRoot(portalDiv).render(
  <GoogleOAuthProvider clientId="269228807455-f5j4lftqf2afdgnvvu6jau1op32dd4a3.apps.googleusercontent.com">
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <DarkModeProvider>
          <WalkthroughProvider>
            <ThemeProvider>
              <MotionConfig reducedMotion="user">
              <ErrorBoundary>
                <RouterProvider router={appRouter} />
              </ErrorBoundary>
              <TourOverlay />
              {/* Opt-in only: the floating toggle otherwise overlaps the
                  sidebar footer (bottom-left) or the Copilot send button
                  (bottom-right) and photobombs demos. Set
                  REACT_APP_SHOW_RQ_DEVTOOLS=true to enable while debugging. */}
              {process.env.REACT_APP_SHOW_RQ_DEVTOOLS === "true" && (
                <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
              )}
              <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
              />
              {/* react-hot-toast container — used by save handlers in
                  TransportConversation, AirlineConversation, etc. The local
                  <Toaster /> in CustomerConversation only mounts when that
                  panel renders, so the manual-query page (which doesn't show
                  the customer panel) used to silently swallow every toast. */}
              <HotToaster position="bottom-center" reverseOrder={false} />
              </MotionConfig>
            </ThemeProvider>
          </WalkthroughProvider>
        </DarkModeProvider>
      </Provider>
    </QueryClientProvider>
  </GoogleOAuthProvider>
);
